import { Activity, useState } from 'react';

// material-ui
import { useColorScheme, useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import BoxContainer from './BoxContainer';
import PresetColor from './PresetColor';
import Layout from './Layout';
import InputFilled from './InputFilled';
import ThemeModeLayout from './ThemeMode';
import SidebarDrawer from './SidebarDrawer';
import MenuOrientation from './MenuOrientation';

import { DEFAULT_THEME_MODE } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import AnimateButton from 'ui-component/extended/AnimateButton';
import SimpleBar from 'ui-component/third-party/SimpleBar';
import useConfig from 'hooks/useConfig';

// assets
import { IconSettings, IconPlus } from '@tabler/icons-react';

// ==============================|| LIVE CUSTOMIZATION ||============================== //
//
// ZERCODE CHANGE: the second tab (FONT STYLE + border radius) is GONE, and with it the tab
// strip — a single panel needs no tabs. Font and border radius are TENANT BRANDING, set once
// on Settings ▸ Branding for everyone in the tenant (Branding.tsx offers the same FONT_OPTIONS
// list as a picker). A per-browser override here competed with that and left two places
// answering "what font is this tenant?".
//
// What stays is genuinely per-user preview state: color scheme, preset color, input style,
// container width, layout, sidebar and menu orientation.

export default function Customization() {
  const theme = useTheme();
  const { resetState } = useConfig();
  const { setMode } = useColorScheme();

  // drawer on/off
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen(!open);
  };

  const handleReset = () => {
    setMode(DEFAULT_THEME_MODE);
    resetState();
  };

  return (
    <>
      {/* toggle button */}
      <Tooltip title="Live Customize">
        <Fab
          component="div"
          onClick={handleToggle}
          size="medium"
          variant="circular"
          color="secondary"
          sx={{
            borderRadius: 0,
            borderTopLeftRadius: '50%',
            borderBottomLeftRadius: '50%',
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '4px',
            top: '25%',
            position: 'fixed',
            right: 10,
            zIndex: 1200,
            boxShadow: theme.vars.customShadows.secondary
          }}
        >
          <AnimateButton type="rotate">
            <IconButton color="inherit" size="large" disableRipple aria-label="live customize">
              <IconSettings />
            </IconButton>
          </AnimateButton>
        </Fab>
      </Tooltip>
      <Drawer anchor="right" onClose={handleToggle} open={open} slotProps={{ paper: { sx: { width: 375 } } }}>
        <Activity mode={open ? 'visible' : 'hidden'}>
          <SimpleBar>
            <MainCard content={false} border={false}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', p: 2.5, gap: 1.5 }}>
                <Typography variant="h5">Theme Customization</Typography>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25 }}>
                  <Button variant="outlined" color="error" size="small" onClick={handleReset}>
                    Reset
                  </Button>
                  <IconButton sx={{ p: 0, color: 'grey.600' }} onClick={handleToggle}>
                    <IconPlus size={24} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
              </Stack>
              <Divider />
              <Grid container spacing={2.5}>
                <Grid size={12}>
                  {/* layout type */}
                  <ThemeModeLayout />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Theme Preset Color */}
                  <PresetColor />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Input Background */}
                  <InputFilled />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Theme Width */}
                  <BoxContainer />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Theme Layout */}
                  <Layout />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Sidebar Drawer */}
                  <SidebarDrawer />
                  <Divider />
                </Grid>
                <Grid size={12}>
                  {/* Menu Orientation */}
                  <MenuOrientation />
                  <Divider />
                </Grid>
              </Grid>
            </MainCard>
          </SimpleBar>
        </Activity>
      </Drawer>
    </>
  );
}
